import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const blank = () => ({ uid: crypto.randomUUID(), value: "", label_ar: "", label_en: "" });
const withUid = (it) => (it && it.uid ? it : { ...it, uid: crypto.randomUUID() });

const AdminStats = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    api
      .get("/admin/content/stats")
      .then((r) => {
        const d = r.data?.data || {};
        const arr = (d.items && d.items.length ? d.items : [blank(), blank(), blank(), blank()]).map(withUid);
        setItems(arr);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        items: items
          .filter((it) => it.value || it.label_ar || it.label_en)
          .map(({ uid, ...rest }) => rest),
      };
      await api.put("/admin/content/stats", payload);
      toast.success("تم حفظ الأرقام بالترتيب الجديد");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setItem = (uid, key, v) => setItems((arr) => arr.map((it) => (it.uid === uid ? { ...it, [key]: v } : it)));
  const addItem = () => setItems((arr) => [...arr, blank()]);
  const delItem = (uid) => setItems((arr) => arr.filter((it) => it.uid !== uid));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setItems((arr) => {
      const oldIdx = arr.findIndex((it) => it.uid === active.id);
      const newIdx = arr.findIndex((it) => it.uid === over.id);
      return arrayMove(arr, oldIdx, newIdx);
    });
  };

  if (loading) return <div className="overline animate-pulse">…</div>;

  return (
    <div data-testid="admin-stats">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="overline mb-3">محتوى الصفحة</div>
      <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone mb-4">الأرقام أسفل رحلتي</h1>
      <p className="text-white/50 text-sm mb-8 font-arabic">اسحب وأفلِت لإعادة ترتيب الأرقام. يظهر الترتيب نفسه على الصفحة الرئيسية.</p>

      <form onSubmit={save} className="space-y-5 max-w-4xl">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((it) => it.uid)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {items.map((it, i) => (
                <SortableRow
                  key={it.uid}
                  item={it}
                  index={i}
                  onChange={setItem}
                  onDelete={() => delItem(it.uid)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <button type="button" onClick={addItem} className="inline-flex items-center gap-2 overline text-white/65 hover:text-gold" data-testid="stats-add">
          <Plus size={12} /> إضافة رقم
        </button>
        <div>
          <button type="submit" disabled={saving} className="btn-gold disabled:opacity-60" data-testid="stats-save">
            <Save size={14} />
            {saving ? "جاري الحفظ…" : "حفظ"}
          </button>
        </div>
      </form>
    </div>
  );
};

const SortableRow = ({ item, index, onChange, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.uid });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`stat-row-${index}`}
      className="grid grid-cols-1 md:grid-cols-[36px_140px_1fr_1fr_auto] gap-3 items-end border border-white/10 bg-ink-soft p-4"
    >
      <button
        type="button"
        className="flex items-center justify-center h-10 w-9 text-white/40 hover:text-gold cursor-grab active:cursor-grabbing touch-none"
        aria-label="drag"
        {...attributes}
        {...listeners}
        data-testid={`stat-drag-${index}`}
      >
        <GripVertical size={16} />
      </button>
      <label className="block">
        <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">الرقم</span>
        <input
          type="text"
          value={item.value}
          onChange={(e) => onChange(item.uid, "value", e.target.value)}
          className="editorial-input"
          dir="ltr"
          placeholder="+20,000"
          data-testid={`stat-value-${index}`}
        />
      </label>
      <label className="block">
        <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">الوصف (عربي)</span>
        <input
          type="text"
          value={item.label_ar}
          onChange={(e) => onChange(item.uid, "label_ar", e.target.value)}
          className="editorial-input"
          placeholder="ساعة بث مباشر"
          data-testid={`stat-ar-${index}`}
        />
      </label>
      <label className="block">
        <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">Label (English)</span>
        <input
          type="text"
          value={item.label_en}
          onChange={(e) => onChange(item.uid, "label_en", e.target.value)}
          className="editorial-input"
          dir="ltr"
          placeholder="Live Broadcast Hours"
          data-testid={`stat-en-${index}`}
        />
      </label>
      <button
        type="button"
        onClick={onDelete}
        className="text-white/50 hover:text-red-400 mb-3"
        title="حذف"
        data-testid={`stat-delete-${index}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
};

export default AdminStats;
